// Base
import { BaseApi } from 'api/base.api'

// Interface
import {
  IMyDocumentApi,
  IMyDocumentApiRequest,
  IMyDocumentApiGetAllAttrs,
  IMyDocumentApiGetAllResponse,
  IMyDocumentApiGetAttrs,
  IMyDocumentApiGetResponse,
  IMyDocumentApiSaveAttrs,
  IMyDocumentApiDownloadDocumentAttrs,
  IMyDocumentApiPreviewDocumentAttrs,
  IMyDocumentApiDownloadDocumentResponse,
  IMyDocumentApiPreviewDocumentResponse,
  IMyDocumentValidateApiDocumentDetailAttrs,
  IMyDocumentApiValidateDocumentDetailResponse,
  IMyDocumentApiDeleteAttrs
} from 'interfaces/back-office/personal-data/my-document/my-document-api.interface'
import { IBaseApiRequestPayload } from 'interfaces/api'

// Constant
import { ApiConstant } from 'constant'

// Utils
import { axios_renderUrl } from 'utils/axios'

export class MyDocumentApi extends BaseApi<IMyDocumentApi> implements IMyDocumentApiRequest {
  readonly url = ApiConstant.MY_DOCUMENT_URL
  private myDocumentUrl = axios_renderUrl(this.url, ApiConstant.BACK_OFFICE)
  private myDocumentUrlByUserId = axios_renderUrl(ApiConstant.USER_URL, ApiConstant.BACK_OFFICE)

  /**
   * @description Get my documents
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IMyDocumentApiGetAllAttrs} payload
   *
   * @return {Promise<IMyDocumentApiGetAllResponse>} Promise<IMyDocumentApiGetAllResponse>
   */
  async myDocument_getMyDocuments(payload: IMyDocumentApiGetAllAttrs): Promise<IMyDocumentApiGetAllResponse> {
    try {
      const response = await this.POST(`${this.myDocumentUrl}/list`, { ...payload.body }, { ...payload?.requestConfig })

      return Promise.resolve(response)
    } catch (err) {
      return Promise.reject(err)
    }
  }

  /**
   * @description Get my documents for a spesific user (used in user detail)
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IMyDocumentApiGetAllAttrs} payload
   *
   * @return {Promise<IMyDocumentApiGetAllResponse>} Promise<IMyDocumentApiGetAllResponse>
   */
  async myDocument_getMyDocumentsByUserId(payload: IMyDocumentApiGetAllAttrs): Promise<IMyDocumentApiGetAllResponse> {
    try {
      const response = await this.POST(`${this.myDocumentUrlByUserId}/documents`, { ...payload.body }, { ...payload?.requestConfig })
      return Promise.resolve(response)
    } catch (err) {
      return Promise.reject(err)
    }
  }

  /**
   * @description Get my document
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IMyDocumentApiGetAttrs} payload
   *
   * @return {Promise<IMyDocumentApiGetResponse>} Promise<IMyDocumentApiGetResponse>
   */
  async myDocument_getMyDocument(payload: IMyDocumentApiGetAttrs): Promise<IMyDocumentApiGetResponse> {
    try {
      const response = await this.POST(`${this.myDocumentUrl}/${payload.params.id}`, {}, { ...payload?.requestConfig })

      return Promise.resolve(response)
    } catch (err) {
      return Promise.reject(err)
    }
  }

  /**
   * @description Save my document
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IMyDocumentApiSaveAttrs} payload
   *
   * @return {Promise<any>} Promise<any>
   */
  async myDocument_saveMyDocument(payload: IMyDocumentApiSaveAttrs): Promise<any> {
    try {
      const requestPayload: IBaseApiRequestPayload = {
        method: 'POST',
        url: payload.params?.id ? `${this.myDocumentUrl}/update/${payload.params.id}` : `${this.myDocumentUrl}/create`,
      }
      const response = await this[requestPayload.method](requestPayload.url, payload.body, { ...payload.requestConfig })

      return Promise.resolve(response)
    } catch (err) {
      return Promise.reject(err)
    }
  }

  /**
   * @description Download document
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IMyDocumentApiDownloadDocumentAttrs} payload
   *
   * @return {Promise<IMyDocumentApiDownloadDocumentResponse>} Promise<IMyDocumentApiDownloadDocumentResponse>
   */
  async myDocument_downloadDocument(
    payload: IMyDocumentApiDownloadDocumentAttrs,
  ): Promise<IMyDocumentApiDownloadDocumentResponse> {
    try {
      const response = await this.POST(
        `${this.myDocumentUrl}/download/${payload.params.blobId}`,
        {},
        {
          ...payload.requestConfig,
          responseType: 'blob',
        },
      )

      return Promise.resolve(response)
    } catch (err) {
      return Promise.reject(err)
    }
  }

  /**
  * @description Preview or Download document given User ID and Blob ID
   *
   * @method GET
   * @access private (only user that logged in)
   *
   * @param {IMyDocumentApiPreviewDocumentAttrs} payload
   *
   * @return {Promise<IMyDocumentApiPreviewDocumentResponse>} Promise<IMyDocumentApiPreviewDocumentResponse>
   */
  async myDocument_previewDocumentByUserId(
    payload: IMyDocumentApiPreviewDocumentAttrs,
  ): Promise<IMyDocumentApiPreviewDocumentResponse> {
    try {
      const response = await this.GET(
        `${this.myDocumentUrlByUserId}/document/download`,
        {
          ...payload.requestConfig,
          params: payload.params,
          responseType: 'blob',
        },
      )

      return Promise.resolve(response)
    } catch (err) {
      return Promise.reject(err)
    }
  }

  /**
   * @description Validate document detail form
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IMyDocumentValidateApiDocumentDetailAttrs} payload
   *
   * @return {Promise<IMyDocumentApiValidateDocumentDetailResponse>} Promise<IMyDocumentApiValidateDocumentDetailResponse>
   */
  async myDocument_validate(
    payload: IMyDocumentValidateApiDocumentDetailAttrs,
  ): Promise<IMyDocumentApiValidateDocumentDetailResponse> {
    try {
      const response = await this.POST(`${this.myDocumentUrl}/validate`, { ...payload.body }, { ...payload.requestConfig })

      return Promise.resolve(response)
    } catch (err) {
      return Promise.reject(err)
    }
  }

  /**
   * @description Delete my document
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IMyDocumentApiDeleteAttrs} payload
   *
   * @return {Promise<any>} Promise<any>
   */
  async myDocument_deleteMyDocument(payload: IMyDocumentApiDeleteAttrs): Promise<any> {
    try {
      const requestPayload: IBaseApiRequestPayload = {
        method: 'DELETE',
        url: `${this.myDocumentUrl}/delete/${payload.params.id}` ,
      }
      const response = await this[requestPayload.method](requestPayload.url, { ...payload.requestConfig })

      return Promise.resolve(response)
    } catch (err) {
      return Promise.reject(err)
    }
  }
}
